import { Link } from 'react-router'
import { Attesa, Avviso } from '../componenti/Pezzi.tsx'
import { Bottone, Etichetta, Modale, Tabella } from '../componenti/base.tsx'
import { useImportazione } from '../componenti/importazione.tsx'

/**
 * Beneficiari > «Importa schede anonimizzate» (email di CIDIS del 31/10/2026: scolaresche formate di cui
 * CIDIS ha solo dati di base), per chi crea schede.
 *
 * Tre passi, come «Importa da Excel» dei mediatori (elenchi/ImportaMediatori.tsx), con lo stesso flusso
 * (componenti/importazione.tsx): il tracciato da scaricare e compilare, con le tendine e le sole sedi in
 * cui chi lo scarica crea schede; l'anteprima riga per riga; l'esito. Ogni riga crea una scheda nuova con
 * «Dati anonimizzati». Le righe da correggere e i possibili doppioni partono esclusi: un doppione si
 * include se e' un'altra persona, perche' le iniziali da sole non bastano a dirlo.
 */

type Riga = {
  numero: number
  valori: Record<string, string>
  stato: 'nuova' | 'errore'
  errori: string[]
  avvisi: string[]
  doppione: { righe: number[]; schede: number; numeri: number[] } | null
  lettura: {
    iniziali: string; genere: string | null; nascita: string | null; paese: string | null; cittadinanza: string | null
    comune: string | null; sede: string | null; progetto: string | null; tipologia: string | null; lingue: string[]
    italiano: string | null; condizione: string | null
  }
}
type Esito = {
  importazione_id: string
  create: { numero: number; id: string; numero_scheda: number; percorso: boolean }[]
  avvisi: number[]
}

const INDIRIZZO = '/persone/anonimizzate/importazione'

export function ImportaAnonimizzate({ chiudi, importate }: {
  chiudi: () => void
  /** Dopo la conferma: l'elenco si ricarica. La finestra resta aperta sull'esito. */
  importate: () => void
}) {
  const {
    fase, errore, inCorso, nomeFile, anteprima, escluse, ricontrollo, esito, ingresso,
    leggi, cambiaInclusione, importa, ricomincia,
  } = useImportazione<Riga, Esito>({
    indirizzo: INDIRIZZO,
    esclusaAllInizio: (r) => r.stato === 'errore' || !!r.doppione,
    importati: importate,
  })

  const righe = anteprima?.righe ?? []
  const incluse = righe.filter((r) => !escluse.has(r.numero))
  const daCorreggere = incluse.filter((r) => r.stato === 'errore')
  const pronte = incluse.filter((r) => r.stato === 'nuova').length
  const doppioni = righe.filter((r) => r.doppione).length

  const titolo = fase === 'foglio' ? 'Importa schede anonimizzate · passo 1 di 3'
    : fase === 'anteprima' ? 'Importa schede anonimizzate · passo 2 di 3, anteprima'
      : 'Importa schede anonimizzate · passo 3 di 3, fatto'

  return (
    <Modale titolo={titolo} larga chiudi={chiudi}
            azioni={fase === 'foglio'
              ? <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
              : fase === 'anteprima'
                ? <>
                  <Bottone variante="discreto" onClick={ricomincia}>Carica un altro foglio</Bottone>
                  <Bottone variante="primario" inCorso={inCorso}
                           disabled={ricontrollo || pronte === 0 || daCorreggere.length > 0}
                           onClick={() => void importa()}>
                    {pronte === 1 ? 'Crea 1 scheda' : `Crea ${pronte} schede`}
                  </Bottone>
                </>
                : <Bottone variante="primario" onClick={chiudi}>Fatto</Bottone>}>
      {fase !== 'esito' && (
        <Avviso tipo="attenzione">
          Solo per persone che non possono firmare l’informativa o di cui CIDIS ha solo dati di base: niente nomi
          interi, niente codici, niente contatti.
        </Avviso>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {fase === 'foglio' && (
        <>
          <p className="nota-modulo">
            Scarica il tracciato, compilalo in Excel (una riga per persona) e caricalo. Prima di creare le schede il
            gestionale ti mostra l’anteprima.
          </p>
          <ul className="operatori-conseguenze">
            <li>
              Obbligatorie: <strong>Iniziali nome</strong>, <strong>Iniziali cognome</strong> (da una a tre lettere,
              «M.» o «M. A.») e <strong>Sede</strong>. Il resto si può lasciare vuoto.
            </li>
            <li>
              Le altre colonne hanno la <strong>tendina</strong>. Della nascita si indica l’anno oppure la fascia
              d’età. Senza nascita, o senza la <strong>conoscenza dell’italiano</strong>, la scheda entra «Da
              completare»: il dato si aggiunge poi dalla scheda. La sede si sceglie fra quelle in cui puoi creare schede.
            </li>
            <li>Il comune di residenza si scrive per nome, con la provincia se ce ne sono due: «Castro (LE)».</li>
            <li>
              Con il <strong>progetto</strong> si indica anche la tipologia: la scheda nasce con un percorso aperto
              oggi nel progetto, senza interventi.
            </li>
            <li>
              Ogni riga crea una scheda nuova con «Dati anonimizzati». Le righe con le stesse iniziali, lo stesso genere,
              la stessa nascita e la stessa sede di un’altra riga o di una scheda già presente sono
              <strong> possibili doppioni</strong>: partono escluse, e le includi tu se sono persone diverse.
            </li>
            <li>Il foglio «Istruzioni» del tracciato spiega ogni colonna, con un esempio.</li>
          </ul>
          <div className="riga operatori-sezione">
            <a className="bottone bottone-secondario" href={`/api${INDIRIZZO}/tracciato`} download>
              Scarica il tracciato
            </a>
            <Bottone variante="primario" inCorso={inCorso} onClick={() => ingresso.current?.click()}>
              Scegli il foglio compilato
            </Bottone>
            <input ref={ingresso} type="file" hidden
                   accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) void leggi(f) }} />
          </div>
          {inCorso && <Attesa testo="Leggo il foglio e controllo le righe…" />}
        </>
      )}

      {fase === 'anteprima' && anteprima && (
        <>
          <p className="nota-modulo">
            {nomeFile}: {righe.length === 1 ? '1 riga' : `${righe.length} righe`}. Da creare: {pronte}.
            {doppioni > 0 && ` Possibili doppioni: ${doppioni}.`}
            {daCorreggere.length > 0 && ` Da correggere: ${daCorreggere.length}.`}
          </p>
          {anteprima.avvisi?.map((a) => <Avviso key={a} tipo="attenzione">{a}</Avviso>)}
          {!!anteprima.colonne_ignorate?.length && (
            <Avviso tipo="informazione">
              Colonne che non uso: {anteprima.colonne_ignorate.map((c) => `«${c}»`).join(', ')}. Nelle schede
              anonimizzate non si registrano note né altri dati.
            </Avviso>
          )}
          {daCorreggere.length > 0 && (
            <Avviso tipo="attenzione">
              {daCorreggere.length === 1 ? 'Una riga inclusa ha' : `${daCorreggere.length} righe incluse hanno`} ancora
              qualcosa da correggere: lasciale fuori, oppure correggi il foglio e caricalo di nuovo.
            </Avviso>
          )}
          {ricontrollo && <Attesa testo="Ricontrollo le righe…" />}
          <Tabella intestazioni={['Importa', 'Riga', 'Persona', 'Dati', 'Esito']}>
            {righe.map((r) => {
              const fuori = escluse.has(r.numero)
              const l = r.lettura
              return (
                <tr key={r.numero} className={fuori ? 'operatori-riga-esclusa' : undefined}>
                  <td data-etichetta="Importa">
                    <input type="checkbox" checked={!fuori} disabled={ricontrollo || inCorso}
                           aria-label={`Importa la riga ${r.numero}`}
                           onChange={(e) => void cambiaInclusione(r.numero, e.target.checked)} />
                  </td>
                  <td data-etichetta="Riga" className="mono">{r.numero}</td>
                  {/* Un solo figlio per cella: sotto gli 860 px la cella e' una riga flessibile. */}
                  <td data-etichetta="Persona"><div>
                    <div className="voce-principale">{r.stato === 'nuova' ? l.iniziali : `${r.valori.cognome} ${r.valori.nome}`}</div>
                    <div className="piccolo tenue">{[l.genere, l.nascita, l.paese && `origine ${l.paese}`].filter(Boolean).join(' · ')}</div>
                  </div></td>
                  <td data-etichetta="Dati"><div>
                    <div>{[l.sede, l.comune].filter(Boolean).join(' · ')}</div>
                    <div className="piccolo tenue">
                      {[l.lingue.join(', '), l.italiano && `italiano ${l.italiano}`, l.condizione].filter(Boolean).join(' · ')}
                    </div>
                    {l.progetto && <div className="piccolo tenue">Percorso: {l.progetto}{l.tipologia ? `, ${l.tipologia}` : ''}</div>}
                  </div></td>
                  <td data-etichetta="Esito"><div>
                    {r.stato === 'errore' && <div><Etichetta tono="critico">Da correggere</Etichetta></div>}
                    {r.stato === 'nuova' && r.doppione && <div><Etichetta tono="attenzione">Possibile doppione</Etichetta></div>}
                    {fuori && r.stato === 'nuova' && <div><Etichetta>Lasciata fuori</Etichetta></div>}
                    {r.stato === 'errore'
                      ? <ul className="operatori-errori-riga">{r.errori.map((x) => <li key={x}>{x}</li>)}</ul>
                      : !fuori && <Etichetta tono="positivo">Nuova</Etichetta>}
                    {r.avvisi.map((x) => <p key={x} className="piccolo tenue">{x}</p>)}
                  </div></td>
                </tr>
              )
            })}
          </Tabella>
        </>
      )}

      {fase === 'esito' && esito && (
        <>
          <Avviso tipo="successo">
            {esito.create.length === 1 ? 'Ho creato 1 scheda' : `Ho creato ${esito.create.length} schede`} con dati
            anonimizzati.
            {esito.create.some((c) => c.percorso)
              && ` In ${esito.create.filter((c) => c.percorso).length} ho aperto il percorso nel progetto.`}
            {esito.avvisi.length > 0 && ` Righe entrate con un avviso: ${esito.avvisi.join(', ')}.`}
          </Avviso>
          {esito.create.length > 0 && (
            <p className="nota-modulo">
              Schede:{' '}
              {esito.create.map((c, i) => (
                <span key={c.id}>{i > 0 && ', '}<Link to={`/persone/${c.id}`}>n. {c.numero_scheda}</Link></span>
              ))}.
            </p>
          )}
        </>
      )}
    </Modale>
  )
}
